import logrocketService from './services/logrocket.service';
import datadogService from './services/datadog.service';
import amplitudeService from './services/amplitude.service';
import type { InitConfig } from './types';

export { HOST_QUERY_TYPE, HOST_REPLY_TYPE, installHostListener, queryHost } from './observabilityHandshake';
export type { HostReply, LogRocketHostReply, DatadogHostReply } from './observabilityHandshake';

export default {
  isInitialized: false,
  isDev: false,
  async init(config: InitConfig) {
    this.isDev = config.isDev;

    if (config.logrocket.enabled) {
      logrocketService.init(config.logrocket.appID, config.logrocket.options);
    }

    if (config.datadog?.enabled) {
      datadogService.init({
        mode: config.datadog.mode,
        applicationId: config.datadog.applicationId,
        clientToken: config.datadog.clientToken,
        service: config.datadog.service,
        env: config.datadog.env,
        version: config.datadog.version,
        sessionSampleRate: config.datadog.sessionSampleRate,
        sessionReplaySampleRate: config.datadog.sessionReplaySampleRate,
        useCrossSiteSessionCookie: config.datadog.useCrossSiteSessionCookie,
        trackSessionAcrossSubdomains: config.datadog.trackSessionAcrossSubdomains
      });
    }

    if (config.amplitude?.enabled) {
      amplitudeService.init(config.amplitude.apiKey);
    }

    this.isInitialized = true;
  },
  identify(uid: string, traits: any) {
    if (!this.isInitialized) {
      return;
    }

    logrocketService.identify(uid, traits);
    datadogService.identify(uid);
    amplitudeService.identify(uid, traits);
  },
  log(...params: any[]) {
    // eslint-disable-next-line no-console
    console.log(...params);
  },
  warn(...params: any[]) {
    console.warn(...params);
  },
  info(...params: any[]) {
    if (this.isDev) {
      // eslint-disable-next-line no-console
      console.log(...params);
    }

    if (!this.isInitialized) {
      return;
    }

    logrocketService.info(...params);

    const message = params
      .map(p =>
        typeof p === 'string'
          ? p
          : (() => {
              try {
                return JSON.stringify(p);
              } catch {
                return String(p);
              }
            })()
      )
      .join(' ');
    datadogService.info(message);
  },
  error(...params: any[]) {
    console.error(...params);

    if (!this.isInitialized) return;

    datadogService.error(params[0]);

    const firstArg = params[0];
    if (firstArg instanceof Error) {
      logrocketService.captureException(firstArg);
    } else if (typeof firstArg === 'string') {
      logrocketService.captureMessage(firstArg);
    } else {
      let message: string;
      try {
        message = JSON.stringify(firstArg) ?? String(firstArg ?? '');
      } catch {
        message = String(firstArg ?? '');
      }
      logrocketService.captureMessage(message);
    }
  },
  track(eventName: string, props = {} as any) {
    if (!this.isInitialized) {
      return;
    }

    const eventProperties = Object.keys(props).reduce((acc, key) => {
      acc[key] = [null, undefined].includes(props[key]) ? '' : props[key];
      return acc;
    }, {});

    logrocketService.track(eventName, eventProperties);
  },
  trackAmplitude(eventName: string, props = {} as any) {
    if (!this.isInitialized) {
      return;
    }
    amplitudeService.track(eventName, props);
  },
  captureException(error: Error) {
    if (!this.isInitialized) {
      return;
    }

    logrocketService.captureException(error);
    datadogService.error(error);
  },
  captureMessage(message: string) {
    if (!this.isInitialized) {
      return;
    }

    logrocketService.captureMessage(message);
  },
  addFeatureFlag(flagName: string, variant: string) {
    if (!this.isInitialized) {
      return;
    }

    datadogService.addFeatureFlag(flagName, variant);
  },
  addMetadata(key: string, value: any) {
    if (!this.isInitialized) {
      return;
    }

    datadogService.addMetadata(key, value);
  }
};
